<template>
  <div class="invoice-recipient">
    <h2 class="invoice-recipient__title">Fakturamottaker</h2>
    <p class="invoice-recipient__description">
      {{ descriptionText }}
    </p>

    <!-- Loading State -->
    <div v-if="isLoading" class="invoice-recipient__loading">
      <Skeleton class="skeleton-loading" height="2rem" />
    </div>

    <!-- Error States -->
    <div v-else-if="error" class="invoice-recipient__error">
      <Message
        v-if="error.type === 'offline'"
        severity="warn"
        :closable="false"
      >
        {{ error.message }}
      </Message>
      <Message
        v-else-if="error.type === 'missing'"
        severity="error"
        :closable="false"
      >
        {{ error.message }}
      </Message>
      <Message v-else severity="error" :closable="false">
        {{ error.message }}
      </Message>
    </div>

    <!-- Success State -->
    <div v-else class="invoice-recipient__details">
      <div v-for="(detail, index) in details" :key="index" class="item">
        <span class="item__label">{{ detail.label }}:</span>
        <span class="item__value">{{ detail.value }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Skeleton from "primevue/skeleton";
import Message from "primevue/message";
import type {
  InvoiceRecipientResult,
  InvoiceRecipientError,
} from "@/composables/useInvoiceRecipient";

// Define props
interface Props {
  bookingNumber: string | number;
  byPassReally?: boolean;
  recipient?: InvoiceRecipientResult | null;
  isLoading?: boolean;
  error?: InvoiceRecipientError | null;
}

const props = withDefaults(defineProps<Props>(), {
  byPassReally: false,
  recipient: null,
  isLoading: false,
  error: null,
});

// Dynamic description text based on byPassReally flag and recipient step
const descriptionText = computed(() => {
  if (!props.byPassReally) {
    return "Faktura sendes til Really! AS via EHF. Merk faktura med referansekoden og beskriv hvilke kostnader som blir fakturert. Dersom EHF ikke er tilgjengelig kan faktura sendes til oppgitt epost.";
  }

  if (props.recipient) {
    const companyName =
      props.recipient.receivingOrganization?.name || "Really! AS";
    if (props.recipient.step === 1) {
      return `Faktura sendes til ${companyName} via EHF basert på forespørsel-spesifikke innstillinger. Merk faktura med referansekoden og beskriv hvilke kostnader som blir fakturert.`;
    } else if (props.recipient.step === 2) {
      return `Faktura sendes til ${companyName} via EHF basert på eiendom-spesifikke innstillinger. Merk faktura med referansekoden og beskriv hvilke kostnader som blir fakturert.`;
    } else if (props.recipient.step === 3) {
      return `Faktura sendes til ${companyName} via EHF basert på organisasjons-innstillinger. Merk faktura med referansekoden og beskriv hvilke kostnader som blir fakturert.`;
    } else if (props.recipient.step === 4) {
      return `Faktura sendes til ${companyName} via EHF basert på tender-spesifikke innstillinger. Merk faktura med referansekoden og beskriv hvilke kostnader som blir fakturert.`;
    } else {
      return `Faktura sendes til ${companyName} via EHF basert på organisasjons-innstillinger. Merk faktura med referansekoden og beskriv hvilke kostnader som blir fakturert.`;
    }
  }

  return "Faktura sendes til Really! AS via EHF. Merk faktura med referansekoden og beskriv hvilke kostnader som blir fakturert. Dersom EHF ikke er tilgjengelig kan faktura sendes til oppgitt epost.";
});

// Prepare details array based on byPassReally flag
const details = computed(() => {
  if (!props.byPassReally) {
    // Default behavior - show Really! AS details
    return [
      { label: "Firmanavn", value: "Really! AS" },
      { label: "Org. nr.", value: "925401919" },
      { label: "Epost", value: "invoice@really.no" },
      { label: "Referanse", value: `#${props.bookingNumber}` },
    ];
  }

  // When bypass is enabled, show custom recipient data
  if (props.recipient) {
    const companyName =
      props.recipient.receivingOrganization?.name || "Really! AS";
    const orgNumber =
      props.recipient.receivingOrganization?.orgNumber || "925401919";

    return [
      { label: "Firmanavn", value: companyName },
      { label: "Org. nr.", value: orgNumber },
      { label: "Epost", value: props.recipient.email },
      {
        label: "Referanse",
        value: `#${props.recipient.externalReference}`,
      },
    ];
  }

  // Fallback during loading or error when bypass is enabled - use default Really! AS details
  return [
    { label: "Firmanavn", value: "Really! AS" },
    { label: "Org. nr.", value: "925401919" },
    { label: "Epost", value: "invoice@really.no" },
    { label: "Referanse", value: `#${props.bookingNumber}` },
  ];
});
</script>

<style scoped lang="scss">
.invoice-recipient {
  $background-control: $color-white;
  $padding-vertical: 24px;
  $padding-horizontal: 32px;
  $title-color: #000;
  $title-font-size: $font-size-base;
  $description-color: #000;
  $description-font-size: $font-size-sm;
  $description-margin: 16px;

  border-radius: 4px;
  background: $background-control;
  padding: $padding-vertical $padding-horizontal;
  margin-left: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.1);

  &__title {
    color: $title-color;
    font-size: $title-font-size;
    font-weight: 600;
    text-align: left;
  }

  &__description {
    color: $description-color;
    font-size: $description-font-size;
    margin: $description-margin 0;
    font-weight: 400;
    text-align: left;
  }

  &__details {
    font-size: $description-font-size;
    width: 100%;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .skeleton-loading {
      width: 100%;
    }

    .loading-text {
      color: #666;
      font-size: $font-size-sm;
      margin: 0;
    }
  }

  &__error {
    width: 100%;

    .banner {
      padding: 12px 16px;
      border-radius: 4px;
      font-size: $font-size-sm;
      margin: 0;

      &--warning {
        background-color: #fff3cd;
        border: 1px solid #ffeaa7;
        color: #856404;
      }

      &--error {
        background-color: #f8d7da;
        border: 1px solid #f5c6cb;
        color: #721c24;
      }
    }
  }
}

.item {
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
}

.item__label {
  font-weight: bold;
}

.item__value {
  margin-left: 8px;
  display: flex;
  text-align: end;
}
</style>
